import * as styles from "./TimePickerTrigger.css";

import { clsx } from "clsx";
import type { KeyboardEvent, PointerEvent } from "react";

import { Popover as BasePopover } from "@base-ui/react/popover";
import type { BaseUIEvent } from "@base-ui/react/types";
import { Icon } from "@frontend/components/atoms/Icon";
import { pointerOffsetIn } from "@frontend/utils/pointerAlign";

import { useTimePickerContext } from "./TimePicker.context";
import type { TimePickerTriggerProps } from "./TimePicker.types";

export function TimePickerTrigger({
  className,
  children,
  onPointerDown,
  onKeyDown,
  ...props
}: TimePickerTriggerProps) {
  const { size, layout, disabled, pointerOffsetRef, open, openPopup, typeKey } =
    useTimePickerContext();

  const isText = layout === "text";

  function handlePointerDown(
    event: BaseUIEvent<PointerEvent<HTMLButtonElement>>,
  ) {
    pointerOffsetRef.current = pointerOffsetIn(
      event.currentTarget,
      event.clientX,
    );

    onPointerDown?.(event);
  }

  function handleKeyDown(event: BaseUIEvent<KeyboardEvent<HTMLButtonElement>>) {
    if (event.key === "Enter" || event.key === " ") {
      pointerOffsetRef.current = 0;
    }

    if (!disabled && (open || /^\d$/.test(event.key))) {
      if (!open) {
        pointerOffsetRef.current = 0;
        openPopup();
      }

      if (typeKey(event.key)) {
        event.preventDefault();
      }
    }

    onKeyDown?.(event);
  }

  return (
    <BasePopover.Trigger
      data-slot="time-picker-trigger"
      disabled={disabled}
      className={clsx(
        isText ? styles.textTrigger : styles.trigger({ size }),
        className,
      )}
      onPointerDown={handlePointerDown}
      onKeyDown={handleKeyDown}
      {...props}
    >
      {children}
      <span className={isText ? styles.textIcon : styles.icon}>
        <Icon name={isText ? "Clock" : "ChevronDown"} />
      </span>
    </BasePopover.Trigger>
  );
}
